<script>

import urlState from "@/router/urlState";
import moment from "moment/moment";
import {mapActions, mapState} from "vuex";
import {getProductionDays} from "@/store/utils";

const dayFormat = 'ddd';
const dateFormat = 'MMM D';

export default {
  name: "ShippingGroupSelector",
  mixins: [urlState],
  async mounted() {
    await this.fetchUpcomingShippingGroups();
  },
  methods: {
    ...mapActions(['fetchUpcomingShippingGroups']),
    closeShippingGroupPicker() {
      this.showDialog = false;
      const {dates, shipping_group} = this.selectedShippingGroup || {}
      console.log('setting dates', dates);
      this.$emit('update:dates', dates);
      this.$emit('update:shippingGroup', shipping_group);
      this.$emit('update', {dates, shipping_group});
      if (this.singleDay) {
        this.$emit('update:date', dates[0]);
      }
    },
    allowedDates(val) {
      const notAllowed = {
        0: true,
        // 2: true,
        // 4: true
      };
      return !notAllowed[moment(val).day()];
    },
    closeDatePicker(shipping_group) {
      this.showDialog = false;
      this.showDatePicker = false;
      const dates = getProductionDays(this.datePickerDate)
      this.$emit('update:dates', dates);
      this.$emit('update:shippingGroup', shipping_group);
      this.$emit('update', {dates, shipping_group});
      if (this.singleDay) {
        this.$emit('update:date', dates[0]);
      }
    },
  },
  watch: {
    showDialog(open) {
      // every close forgets the future shipping groups, however the dialog was dismissed
      if (!open) {
        this.showFutureShippingGroups = false;
      }
    },
  },
  computed: {
    ...mapState(['shippingGroups']),
    shippingGroupNumber(){
      if (this.shippingGroup && this.dates && this.dates.length> 0) {
        const shippingGroupMap = {
          shipMon: 1,
          localMon: 2,
          shipThu: 3,
          localThu: 4
        }
        const dates = this.dates || [];
        const dateFrom = moment(dates[0]);
        const startDay = dateFrom.format(dayFormat);
        return shippingGroupMap[`${this.shippingGroup}${startDay}`] || '?';
      } else {
        return '';
      }
    },
    upcomingShippingGroups() {
      function formatShippingGroup(p) {
        const shippingGroupMap = {
          shipMon: 1,
          localMon: 2,
          shipThu: 3,
          localThu: 4
        }
        const dates = p.dates.sort();
        const dateFrom = moment(dates[0]);
        const dateTo = moment(dates[dates.length - 1]);
        const startDay = dateFrom.format(dayFormat);
        const shippingGroupNumber = shippingGroupMap[`${p.shipping_group}${startDay}`] || '?';
        return {
          number: shippingGroupNumber,
          text: `${p.shipping_group}: ${startDay}-${dateTo.format(dayFormat)}`
            + ` (${dateFrom.format(dateFormat)}-${dateTo.format(dateFormat)})`,
          buttonText: `${p.shipping_group}: ${startDay}-${dateTo.format(dayFormat)}`
        };
      }

      const sortKey = a => `${a.dates[0]}-${a.number} `;
      let shippingGroups = (this.shippingGroups || []).slice();
      if (this.shippingGroupFilter) {
        shippingGroups = shippingGroups.filter(p => p.shipping_group === this.shippingGroupFilter);
      }
      return shippingGroups.map(p => ({...p, ...formatShippingGroup(p)}))
        .sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
    },
    shippingGroupColumns() {
      const grouped = {};
      this.upcomingShippingGroups.forEach(p => {
        const dateFrom = moment(p.dates[0]);
        const dateTo = moment(p.dates[p.dates.length - 1]);
        const day = dateFrom.format(dayFormat);
        const key = dateFrom.format('YYYY-MM-DD');
        
        if (!grouped[key]) {
          grouped[key] = {
            title: `${day}-${dateTo.format(dayFormat)} (${dateFrom.format(dateFormat)}-${dateTo.format(dateFormat)})`,
            shippingGroups: []
          };
        }
        grouped[key].shippingGroups.push(p);
      });
      
      return Object.values(grouped).sort((a, b) => a.shippingGroups[0].dates[0].localeCompare(b.shippingGroups[0].dates[0]));
    },
    /** the soonest shipping week only, unless the future shipping groups have been asked for */
    visibleShippingGroupColumns() {
      return this.showFutureShippingGroups ? this.shippingGroupColumns : this.shippingGroupColumns.slice(0, 1);
    },
    hasFutureShippingGroups() {
      return this.shippingGroupColumns.length > 1;
    },
    dateFrom() {
      return [...(this.dates || [])].sort()[0];
    },
    dateTo() {
      return [...(this.dates || [])].sort().reverse()[0];
    },
    shippingGroupTypes() {
      return [...new Set(this.shippingGroups.map(p => p.shipping_group))];
    },
    datesFormatted() {
      const format = dateFormat;
      if (!this.dateFrom) {
        return '';
      } else if (this.dateFrom === this.dateTo) {
        return `${moment(this.dateFrom).format(format)}`;
      } else {
        return `${moment(this.dateFrom).format(format)} - ${moment(this.dateTo).format(format)}`
      }
    },
    selectedShippingGroupText() {
      if (this.shippingGroup && this.dates && this.dates.length > 0) {
        const dates = this.dates.slice().sort();
        const dateFrom = moment(dates[0]);
        const dateTo = moment(dates[dates.length - 1]);
        const startDay = dateFrom.format(dayFormat);
        const endDay = dateTo.format(dayFormat);
        return `${this.shippingGroup}: ${startDay}-${endDay} (${dateFrom.format(dateFormat)}-${dateTo.format(dateFormat)})`;
      }
      return '';
    },
  },
  data() {
    return {
      datePickerDate: null,
      showDialog: false,
      showFutureShippingGroups: false,
      selectedShippingGroup: null,
      showDatePicker: null
    }
  },
  props: {
    shippingGroup: {required: false},
    dates: {type: Array},
    date: {type: String},
    singleDay: {type: Boolean, default: false},
    shippingGroupFilter: {type: String, default: null, required: false}
  }
}

</script>

<template>
  <div :class="`${shippingGroup}-shipping-group`">
    <v-text-field
      class="px-2"
      :value="selectedShippingGroupText"
      label="Shipping Group - Filter by delivery logistics"
      hide-details
      single-line
      readonly
      :prefix="shippingGroupNumber?`(${shippingGroupNumber})`:''"
      @click="showDialog=true"
      append-icon="mdi-calendar"
      prepend-inner-icon="mdi-truck-delivery"
      style="min-width: 350px;"
    />
    <v-dialog
      v-model="showDialog"
      ref="dialog"
      width="auto"
      class="debugcss"
    >
      <v-card v-if="!showDatePicker">
        <v-card-title>
          <v-spacer/>
          Select Shipping Group
          <v-spacer/>
          <v-btn icon @click="showDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-actions>
          <v-row>
            <v-col
              :cols="visibleShippingGroupColumns.length > 1 ? 6 : 12"
              v-for="(column, i) of visibleShippingGroupColumns"
              v-bind:key="i"
            >
              <div class="text-center mb-2" style="white-space: nowrap; font-size: 0.8em;">
                <strong>{{ column.title }}</strong>
              </div>
              <div class="flex-column">
                <div class="mb-6" v-for="(p,j) of column.shippingGroups" v-bind:key="j">
                  <v-btn
                    width="100%"
                    :class="`${p.shipping_group}-shipping-group`"
                    outlined large @click="selectedShippingGroup = p; closeShippingGroupPicker()"
                  >
                    <v-chip style="border-width: thick" v-if="p.shipping_group==='ship'" outlined color="blue">
                      {{ p.number }}
                    </v-chip>
                    <v-chip style="border-width: thick" v-if="p.shipping_group==='local'" outlined>{{
                        p.number
                      }}
                    </v-chip>
                    <div class="text-right pl-4" style="width: 100%">
                      {{ p.buttonText }}
                    </div>
                  </v-btn>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-actions>
        <v-card-actions>
          <v-spacer/>
          <v-btn outlined x-small @click="showDatePicker=true">Show Other Dates</v-btn>
          <v-btn
            v-if="hasFutureShippingGroups"
            class="ml-2"
            outlined
            x-small
            @click="showFutureShippingGroups = !showFutureShippingGroups"
          >
            {{ showFutureShippingGroups ? 'Hide Future Shipping Groups' : 'Show Future Shipping Groups' }}
          </v-btn>
          <v-spacer/>
        </v-card-actions>
      </v-card>
      <v-card v-if="showDatePicker">
        <v-card-text>
          <v-date-picker
            v-model="datePickerDate"
            no-title
            :allowed-dates="allowedDates"
            @close="closeDatePicker"
          ></v-date-picker>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn v-for="p of shippingGroupTypes" v-bind:key="p"
                 @click="closeDatePicker(p)"
          >
            {{ p }}
          </v-btn>
          <v-spacer/>
          <!--          <v-btn @click="closeDatePicker">Close</v-btn>-->
        </v-card-actions>
        <v-card-actions class="mt-10">
          <v-spacer/>
          <v-btn outlined x-small @click="showDatePicker=false">Show Upcoming Shipping Groups</v-btn>
          <v-spacer/>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>

</style>